import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { eur, dateIt } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import StatusBadge from "@/components/app/StatusBadge";
import { toast } from "sonner";
import { paymentMethodLabel, sumOrdersTotal } from "@/lib/orders";
import { Download, Trash2, Check, ChevronsUpDown, X, Filter, RotateCcw, PackageCheck, FileSpreadsheet } from "lucide-react";

const EMPTY_FILTERS = { status: "all", partnerId: "all", date_from: "", date_to: "", evaded: "all" };

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [partners, setPartners] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [partnerPickerOpen, setPartnerPickerOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // { id, order_number }
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (applied.status !== "all") params.status = applied.status;
    if (applied.partnerId !== "all") params.partner_id = applied.partnerId;
    if (applied.date_from) params.date_from = applied.date_from;
    if (applied.date_to) params.date_to = applied.date_to;
    if (applied.evaded !== "all") params.evaded = applied.evaded;
    const { data } = await api.get("/orders", { params });
    setOrders(data);
  }, [applied]);

  const toggleEvaded = async (o) => {
    try {
      await api.post(`/orders/${o.id}/evaded`, { value: !o.evaded });
      toast.success(o.evaded ? `Ordine ${o.order_number} riaperto` : `Ordine ${o.order_number} segnato come Evaso`);
      load();
    } catch (e) { toast.error("Errore aggiornamento ordine"); }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/partners").then((r) => setPartners(r.data)).catch(() => setPartners([])); }, []);
  // Marca tutti gli ordini "inviato" come visti all'ingresso in pagina
  useEffect(() => {
    (async () => {
      try { await api.post("/orders/mark-all-seen"); } catch (e) { /* noop */ }
      window.dispatchEvent(new Event("badges:refresh"));
    })();
  }, []);

  const selectedPartnerName = useMemo(() => {
    if (filters.partnerId === "all") return "Tutti i partner";
    const p = partners.find((x) => x.id === filters.partnerId);
    return p?.company_name || "Partner selezionato";
  }, [filters.partnerId, partners]);

  const applyFilters = () => setApplied({ ...filters });
  const resetFilters = () => { setFilters(EMPTY_FILTERS); setApplied(EMPTY_FILTERS); };
  const totalFiltered = sumOrdersTotal(orders);
  const totalSaving = orders.filter((o) => o.status !== "annullato").reduce((s, o) => s + Number(o.saving_total || 0), 0);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/orders/${confirmDelete.id}`);
      toast.success(`Ordine ${confirmDelete.order_number} eliminato`);
      setConfirmDelete(null); await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'eliminazione dell'ordine");
    } finally { setDeleting(false); }
  };

  const exportHref = useCallback((ext = "") => {
    const url = new URL(`${API}/export/orders${ext}`, window.location.origin);
    if (applied.partnerId !== "all") url.searchParams.set("partner_id", applied.partnerId);
    if (applied.status !== "all") url.searchParams.set("status", applied.status);
    if (applied.date_from) url.searchParams.set("date_from", applied.date_from);
    if (applied.date_to) url.searchParams.set("date_to", applied.date_to);
    return url.toString();
  }, [applied]);

  return (
    <div className="space-y-6" data-testid="admin-orders">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Ordini partner</h1>
          <p className="text-slate-500 mt-1">Approva, verifica e gestisci gli ordini di tutti gli affiliati.</p>
        </div>
        <div className="flex gap-2">
          <a href={exportHref()} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" data-testid="export-orders-csv"><Download className="w-4 h-4 mr-1" /> Esporta CSV</Button>
          </a>
          <a href={exportHref(".xlsx")} target="_blank" rel="noopener noreferrer">
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="export-orders-xlsx"><FileSpreadsheet className="w-4 h-4 mr-1" /> Esporta Excel</Button>
          </a>
        </div>
      </div>

      <Card className="border-slate-200" data-testid="orders-filters-card">
        <CardContent className="p-4 flex flex-wrap items-end gap-3">
          <div className="min-w-[220px]">
            <Label className="text-xs">Partner</Label>
            <Popover open={partnerPickerOpen} onOpenChange={setPartnerPickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" role="combobox" aria-expanded={partnerPickerOpen} className="w-64 justify-between" data-testid="admin-orders-partner-filter">
                  <span className="truncate">{selectedPartnerName}</span>
                  <ChevronsUpDown className="w-4 h-4 opacity-60 shrink-0 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-0" align="start">
                <Command>
                  <CommandInput placeholder="Cerca partner…" data-testid="admin-orders-partner-search" />
                  <CommandList>
                    <CommandEmpty>Nessun partner trovato.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem value="__all__" onSelect={() => { setFilters({ ...filters, partnerId: "all" }); setPartnerPickerOpen(false); }} data-testid="admin-orders-partner-option-all">
                        <Check className={`mr-2 h-4 w-4 ${filters.partnerId === "all" ? "opacity-100" : "opacity-0"}`} />
                        Tutti i partner
                      </CommandItem>
                      {partners.map((p) => (
                        <CommandItem key={p.id} value={`${p.company_name} ${p.email}`} onSelect={() => { setFilters({ ...filters, partnerId: p.id }); setPartnerPickerOpen(false); }} data-testid={`admin-orders-partner-option-${p.id}`}>
                          <Check className={`mr-2 h-4 w-4 ${filters.partnerId === p.id ? "opacity-100" : "opacity-0"}`} />
                          <div className="flex-1 min-w-0">
                            <div className="truncate">{p.company_name}</div>
                            <div className="text-xs text-slate-500 truncate">{p.email}</div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <div className="min-w-[180px]">
            <Label className="text-xs">Stato</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger data-testid="admin-orders-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli stati</SelectItem>
                <SelectItem value="bozza">Bozza</SelectItem>
                <SelectItem value="inviato">Inviato</SelectItem>
                <SelectItem value="in_verifica">In verifica</SelectItem>
                <SelectItem value="confermato">Confermato</SelectItem>
                <SelectItem value="completato">Completato</SelectItem>
                <SelectItem value="annullato">Annullato</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[160px]">
            <Label className="text-xs">Lavorazione</Label>
            <Select value={filters.evaded} onValueChange={(v) => setFilters({ ...filters, evaded: v })}>
              <SelectTrigger data-testid="admin-orders-evaded-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="no">Da evadere</SelectItem>
                <SelectItem value="si">Evasi</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[160px]">
            <Label className="text-xs">Data da</Label>
            <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="orders-date-from" />
          </div>
          <div className="min-w-[160px]">
            <Label className="text-xs">Data a</Label>
            <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="orders-date-to" />
          </div>
          <Button onClick={applyFilters} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="orders-filter-apply">
            <Filter className="w-4 h-4 mr-1" /> Filtra
          </Button>
          <Button onClick={resetFilters} variant="outline" data-testid="orders-filter-reset">
            <RotateCcw className="w-4 h-4 mr-1" /> Azzera filtri
          </Button>
        </CardContent>
      </Card>

      <Card className="border-emerald-200 bg-emerald-50/40" data-testid="orders-total-card">
        <CardContent className="p-4 flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm text-slate-600">
            <span className="font-semibold text-slate-900">{orders.length}</span> ordini visualizzati
          </div>
          <div className="flex gap-8 flex-wrap">
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Risparmio partner nel periodo</div>
              <div className="font-display font-extrabold text-2xl text-emerald-700" data-testid="orders-saving-value">{eur(totalSaving)}</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">Totale Ordini Visualizzati</div>
              <div className="font-display font-extrabold text-2xl text-emerald-700" data-testid="orders-total-value">{eur(totalFiltered)}</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Ordine</TableHead><TableHead>Partner</TableHead><TableHead>Data</TableHead>
            <TableHead>Voci</TableHead><TableHead>Iscritti</TableHead><TableHead>Prezzo standard</TableHead><TableHead>Sconto</TableHead><TableHead>Totale</TableHead>
            <TableHead>Stato</TableHead><TableHead>Pagamento</TableHead>
            <TableHead>Modalità di Pagamento</TableHead><TableHead>Evaso</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {orders.map((o) => (
              <TableRow key={o.id} data-testid={`admin-order-${o.order_number}`}>
                <TableCell><Link to={`/admin/ordini/${o.id}`} className="font-semibold text-[#0F2942] hover:underline">{o.order_number}</Link></TableCell>
                <TableCell>{o.partner_name}</TableCell>
                <TableCell>{dateIt(o.created_at)}</TableCell>
                <TableCell>{(o.items || []).length}</TableCell>
                <TableCell>{o.enrollments_count}</TableCell>
                <TableCell className="text-xs font-mono whitespace-nowrap" data-testid={`admin-order-standard-${o.order_number}`}>{o.standard_total != null ? eur(o.standard_total) : "—"}</TableCell>
                <TableCell className="text-xs font-mono whitespace-nowrap text-emerald-700" data-testid={`admin-order-saving-${o.order_number}`}>{o.saving_total != null ? eur(o.saving_total) : "—"}</TableCell>
                <TableCell className="font-semibold">{eur(o.total)}</TableCell>
                <TableCell><StatusBadge status={o.status} /></TableCell>
                <TableCell><StatusBadge status={o.payment_status} /></TableCell>
                <TableCell className="text-xs text-slate-700" data-testid={`admin-order-payment-method-${o.order_number}`}>
                  {paymentMethodLabel(o.payment_method)}
                </TableCell>
                <TableCell>
                  {o.evaded ? (
                    <button onClick={() => toggleEvaded(o)} title={`Evaso da ${o.evaded_by || ""} il ${dateIt(o.evaded_at)} · clic per riaprire`} className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase bg-emerald-100 text-emerald-800 hover:bg-emerald-200" data-testid={`admin-order-evaded-badge-${o.order_number}`}>
                      <PackageCheck className="w-3.5 h-3.5" /> Evaso
                    </button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => toggleEvaded(o)} data-testid={`admin-order-evade-${o.order_number}`}>Segna Evaso</Button>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => setConfirmDelete({ id: o.id, order_number: o.order_number })} data-testid={`admin-order-delete-${o.order_number}`} aria-label={`Elimina ordine ${o.order_number}`}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {orders.length === 0 && <TableRow><TableCell colSpan={13} className="text-center text-slate-500 py-10">Nessun ordine.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
        <AlertDialogContent data-testid="admin-order-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina ordine {confirmDelete?.order_number}</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare questo ordine? L'operazione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="admin-order-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="admin-order-delete-confirm-button">
              {deleting ? "Eliminazione…" : "Elimina"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
